import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import StatusPill from '../../../shared/designSystem/components/StatusPill';

// =============================================================================
// StatusTag — Campaign-specific status derivation + tone/label mapping.
//
// Delegates rendering to the shared `<StatusPill>` primitive; owns the
// Campaign-domain logic: the deriveStatus date-based state machine (LIVE /
// QUEUED / ATTRIBUTION / FINISHED), the status-override table, and the
// status → `{ tone, label }` mapping in STATUS_PILL below. Other domains
// (SEO, PaidSearch) have their own StatusTag wrappers with their own
// mappings.
//
// Legacy `.status-badge-enhanced` / `.status-badge-{STATUS}` classes are
// forwarded via the `status` prop of `<StatusPill>` so `CampaignsTable.scss`
// scoped overrides still fire inside `.campaigns-table-wrapper`.
// =============================================================================

const STATUS_PILL = {
  LIVE:            { tone: 'emerald',  label: 'Live' },
  FINISHED:        { tone: 'neutral',  label: 'Finished' },
  QUEUED:          { tone: 'amber',    label: 'Queued' },
  ATTRIBUTION:     { tone: 'sky',      label: 'In Attr.' },
  DRAFT:           { tone: 'neutral',  label: 'Draft' },
  PENDING:         { tone: 'primary',  label: 'Pending' },
  PAUSED:          { tone: 'amber',    label: 'Paused' },
  DEMO:            { tone: 'neutral',  label: 'Demo' },
  LAUNCHING:       { tone: 'primary',  label: 'Launching' },
  APPROVED:        { tone: 'emerald',  label: 'Approved' },
  REJECTED:        { tone: 'critical', label: 'Rejected' },
  PENDING_CLIENT:  { tone: 'amber',    label: 'Pending Client' },
  ARCHIVED:        { tone: 'neutral',  label: 'Archived' },
  PAST:            { tone: 'neutral',  label: 'Past' },
};

export const TagWithConfig = ({ tone, label, status }) => (
  <StatusPill tone={tone} label={label} status={status} />
);

const getStatusOverride = (statusCampaign, checkStatus) => {
  if (statusCampaign === 'DRAFT' || checkStatus === 'DRAFT') return 'DRAFT';
  if (statusCampaign === 'PENDING' || checkStatus === 'PENDING') return 'PENDING';
  if (statusCampaign === 'LIVE_PENDING' || checkStatus === 'LIVE_PENDING') return 'PENDING';
  if (statusCampaign === 'PAUSED_PENDING' || checkStatus === 'PAUSED_PENDING') return 'PENDING';
  if (statusCampaign === 'PAUSED' || checkStatus === 'PAUSED') return 'PAUSED';
  if (statusCampaign === 'LAUNCHING_PAUSED' || checkStatus === 'LAUNCHING_PAUSED') return 'PAUSED';
  if (statusCampaign === 'LAUNCHING_LIVE' || checkStatus === 'LAUNCHING_LIVE') return 'LIVE';
  if (statusCampaign === 'DEMO' || checkStatus === 'DEMO') return 'DEMO';
  if (statusCampaign === 'PENDING_CLIENT' || checkStatus === 'PENDING_CLIENT')
    return 'PENDING_CLIENT';
  if (statusCampaign === 'APPROVED' || checkStatus === 'APPROVED') return 'APPROVED';
  if (statusCampaign === 'LIVE_APPROVED' || checkStatus === 'LIVE_APPROVED') return 'APPROVED';
  if (statusCampaign === 'PAUSED_APPROVED' || checkStatus === 'PAUSED_APPROVED') return 'APPROVED';
  if (statusCampaign === 'REJECTED' || checkStatus === 'REJECTED') return 'REJECTED';
  if (statusCampaign === 'ARCHIVED' || checkStatus === 'ARCHIVED') return 'ARCHIVED';
  if (statusCampaign === 'PAST' || checkStatus === 'PAST') return 'PAST';
  return null;
};

export const deriveStatus = (
  startDate,
  endDate,
  attributionWindow,
  statusCampaign,
  checkStatus
) => {
  /**
   * Dates are modified to be able to be checked with
   * the moment comparison functions.
   *
   * Setting the endDate to be the end of day and startDate
   * to be the start of day is needed to make it a "range"
   *
   * Also, setting up the current date in the middle will avoid the edge case
   * of checking it start of day or end of day.
   */
  const currentDateTime = moment().hours(12).minutes(0).seconds(0).milliseconds(0);
  startDate = startDate
    ? moment(startDate, moment.ISO_8601).hours(0).minutes(0).seconds(0).milliseconds(0)
    : null;
  endDate = endDate
    ? moment(endDate, moment.ISO_8601).hours(23).minutes(59).seconds(59).milliseconds(0)
    : null;

  // Check for status overrides first (except DEMO which needs date logic)
  const statusOverride = getStatusOverride(statusCampaign, checkStatus);
  if (statusOverride && statusOverride !== 'DEMO') {
    return statusOverride;
  }

  // Date-based logic with DEMO special handling
  if (
    currentDateTime.isBetween(startDate, endDate) ||
    checkStatus === 'DEMO' ||
    statusCampaign === 'DEMO'
  ) {
    if (statusCampaign === 'DEMO' || checkStatus === 'DEMO') return 'DEMO';
    return 'LIVE';
  }

  if (currentDateTime.isAfter(endDate)) {
    const attributionWindowEndDate = moment(endDate).add(attributionWindow, 'days');
    if (currentDateTime.isAfter(attributionWindowEndDate)) return 'FINISHED';
    return 'ATTRIBUTION';
  }

  if (currentDateTime.isBefore(startDate)) {
    return 'QUEUED';
  }

  return 'LIVE';
};

const StatusTag = ({ startDate, endDate, attributionWindow, statusCampaign, checkStatus }) => {
  const status = useMemo(
    () => deriveStatus(startDate, endDate, attributionWindow, statusCampaign, checkStatus),
    [startDate, endDate, attributionWindow, statusCampaign, checkStatus]
  );
  const config = STATUS_PILL[status] || STATUS_PILL.DRAFT;
  return <TagWithConfig {...config} status={status} />;
};

StatusTag.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  attributionWindow: PropTypes.number,
};

export default StatusTag;
